<template>
    <v-container
        id="tickets-page"
        fluid
        :pa-0="this.$vuetify.breakpoint.xs"
    >
        <v-row v-if="this.$vuetify.breakpoint.xs">
            <!-- Mobile Company Name Header -->
            <v-col>
                <div class="mobile-company-header pa-3">
                    {{ companyName }}
                </div>
                <v-divider class="mt-2" />
            </v-col>
        </v-row>       
        <v-row class="mt-0">
            <!-- Ticket Tabs -->
            <v-col class="py-1">
                <v-tabs
                    ref="tabs"
                    :key="JSON.stringify(ticketTabs)"
                    v-model="tabModel"
                    class="ticket-tabs"
                    active-class="active-ticket-tab"
                    show-arrows                                   
                >
                    <v-tab
                        v-for="ticketTab of ticketTabs"
                        :key="ticketTab.tabId"
                        :disabled="loading"
                        @click="selectedTab(ticketTab.tabId,ticketTab.label)"
                    >
                        <v-icon v-if="ticketTab.isClipboard">
                            mdi-bookmark
                        </v-icon>
                        <label class="subtitle-2">{{ ticketTab.label }}</label>
                        <v-chip
                            class="ml-2 text-caption ticket-chip"
                            outlined
                        >
                            {{ ticketTab.isClipboard ? clipboardItems.length : ticketTab.ticketCount }}
                        </v-chip>
                        <v-icon
                            v-if="ticketTab.removeable"
                            class="ml-2"
                            @click.stop="showConfirmRemoveTab(ticketTab)"
                        >
                            mdi-close
                        </v-icon>
                    </v-tab>
                    <v-btn
                        depressed
                        height="inherit"
                        @click="handelOpenAddTicketTabModel"
                    >
                        <v-icon> mdi-plus </v-icon>
                    </v-btn>
                </v-tabs>
            </v-col>
        </v-row>

        <v-row
            class="mt-0"
        >
            <v-col
                v-if="showSearchFilter"
                class="open-search-filter"
                :class="$vuetify.breakpoint.xs ? 'open-search-mobile-filter' : ''"
            >
                <div>
                    <div
                        class="filter-header d-flex align-center"
                        :class="$vuetify.breakpoint.xs ? 'filter-mobile-header' : ''"
                    >
                        <div
                            :class="[$vuetify.breakpoint.mdAndUp ? 'filter-title':'filter-title-mobile']"
                        >
                            Search Tickets
                        </div>
                        <div
                            class="clear-filter"
                            @click="clearFiltersAndApply"
                        >
                            <a>Clear</a>
                        </div>
                        <v-icon
                            class="close-filter"
                            :class="$vuetify.breakpoint.xs ? 'close-mobile-filter': ''"
                            @click="showSearchFilter=false"
                        >
                            mdi-close
                        </v-icon>
                    </div>
                    <div
                        class="filter-body"
                        :class="$vuetify.breakpoint.xs ? 'filter-mobile-body' : ''"
                    >
                        <v-col>
                            <!-- Stage -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Stage"
                                    :items="stages"
                                    label="Stage"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Center (hide if only 1 center) -->
                            <v-row
                                v-show="!companyUsesSingleCenter"
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Center"
                                    :items="companyCenters"
                                    item-text="name"
                                    item-value="value"
                                    label="Center"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Ticket Number -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.TicketNumber"
                                    label="Ticket Number"
                                    placeholder="Ticket Number"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Member Code -->
                            <v-row
                                v-show="!companyUsesSingleMemberCode"
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.MemberCode"
                                    :items="companyMemberCodes"
                                    item-text="member_code"
                                    item-value="member_code"
                                    label="Member"
                                    dense
                                    outlined
                                    multiple
                                >
                                    <template v-slot:prepend-item>
                                        <v-list-item
                                            ripple
                                            @mousedown.prevent
                                            @click="selectAllMemberCode"
                                        >
                                            <v-list-item-action>
                                                <v-icon :color="searchFilter.MemberCode.length > 0 ? 'indigo darken-4' : ''">
                                                    {{ selectMemberCodeIcon }}
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Select All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mt-2" />
                                    </template>
                                </v-select>
                            </v-row>
                            <!-- Priority -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Priority"
                                    :items="companyTicketPriorities"
                                    item-text="text"
                                    item-value="abbrev"
                                    label="Priority"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Risk -->
                            <v-row
                                cols="12"
                                sm="6"
                                v-show="useInsightLogixRisk"
                            >
                                <v-select
                                    v-model="searchFilter.Risk"
                                    :items="riskOptions"
                                    label="Risk"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Type -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.TicketType"
                                    :items="companyTicketTypes"
                                    item-text="text"
                                    item-value="abbrev"
                                    label="Type"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Category (hide if not used) -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Category"
                                    :items="companyTicketCategories"
                                    label="Category"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Seq Date -->
                            <v-row
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-menu
                                    ref="seqDateMenu"
                                    v-model="seqDateMenu"
                                    :close-on-content-click="false"
                                    :return-value.sync="seqDate"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="SeqDateText"
                                            label="Seq Date"
                                            append-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            outlined
                                            dense
                                            v-on="on"
                                        />
                                    </template>
                                    <v-date-picker
                                        v-model="searchFilter.SeqDate"
                                        no-title
                                        range
                                        scrollable
                                    >
                                        <v-spacer />
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="seqDateMenu = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.seqDateMenu.save(seqDate)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-row>
                            <!-- Work Date -->
                            <v-row
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-menu
                                    ref="workDateMenu"
                                    v-model="workDateMenu"
                                    :close-on-content-click="false"
                                    :return-value.sync="searchFilter.WorkDate"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="WorkDateText"
                                            label="Work Date"
                                            append-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            outlined
                                            dense
                                            v-on="on"
                                        />
                                    </template>
                                    <v-date-picker
                                        v-model="searchFilter.WorkDate"
                                        no-title
                                        range
                                        scrollable
                                    >
                                        <v-spacer />
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="workDateMenu = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.workDateMenu.save(searchFilter.WorkDate)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-row>
                            <!-- Updated Date -->
                            <v-row
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-menu
                                    ref="updatedMenu"
                                    v-model="updatedMenu"
                                    :close-on-content-click="false"
                                    :return-value.sync="searchFilter.UpdateDate"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="UpdateDateText"
                                            label="Updated"
                                            append-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            outlined
                                            dense
                                            v-on="on"
                                        />
                                    </template>
                                    <v-date-picker
                                        v-model="searchFilter.UpdateDate"
                                        no-title
                                        range
                                        scrollable
                                    >
                                        <v-spacer />
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="updatedMenu = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.updatedMenu.save(searchFilter.UpdateDate)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-row>
                            <!-- Due -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Due"
                                    :items="dues"
                                    label="Due"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Locator -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-autocomplete
                                    v-model="searchFilter.Locator"
                                    :items="companyLocators"
                                    item-text="name"
                                    item-value="value"
                                    label="Locator"
                                    dense
                                    outlined
                                />
                            </v-row>
                            <!-- Counties -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-model="searchFilter.Counties"
                                    :items="companyCounties"
                                    item-text="name"
                                    item-value="value"
                                    label="Counties"
                                    dense
                                    multiple
                                    small-chips
                                    outlined
                                    @change="changeCounties"
                                >
                                    <template v-slot:prepend-item>
                                        <v-list-item
                                            ripple
                                            :enabled="searchFilter.Counties.length > 0"
                                            @mousedown.prevent
                                            @click="clearSelectedCounties"
                                        >
                                            <v-list-item-action>
                                                <!-- <v-icon :color="searchFilter.MemberCode.length > 0 ? 'indigo darken-4' : ''">
                                                    {{ selectMemberCodeIcon }}
                                                </v-icon> -->
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Clear All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mt-2" />
                                    </template>
                                </v-select>
                            </v-row>
                            <!--  -->
                            <!--  -->
                            <!--  -->
                            <!--  -->
                            <!--  -->
                            <!--  -->
                            <!-- Place -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Place"
                                    label="Place"
                                    placeholder="Place"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Subdivision -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Subdivision"
                                    label="Subdivision"
                                    placeholder="Subdivision"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Address -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Address"
                                    label="Address"
                                    placeholder="Address"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Street -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Street"
                                    label="Street"
                                    placeholder="Street"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Cross -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Cross"
                                    label="Cross Street"
                                    placeholder="Cross Street"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Company -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Company"
                                    label="Company"
                                    placeholder="Company"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Caller -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Caller"
                                    label="Caller Name"
                                    placeholder="Caller Name"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Done For -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.DoneFor"
                                    label="Done For"
                                    placeholder="Done For"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Work Type -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.WorkType"
                                    label="Work Type"
                                    placeholder="Work Type"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Location (hide if not used)-->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Location"
                                    label="Location"
                                    placeholder="Location"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Grid -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Grid"
                                    label="Grid"
                                    placeholder="Grid"
                                    outlined
                                    dense
                                />
                            </v-row>
                            <!-- Comments -->
                            <v-row
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="searchFilter.Comment"
                                    label="Comments"
                                    placeholder="Comments"
                                    outlined
                                    dense
                                />
                            </v-row>
                        </v-col>
                    </div>
                    <div
                        class="filter-footer d-flex"
                        :class="$vuetify.breakpoint.xs ? 'filter-mobile-footer' : ''"
                    >
                        <v-toolbar
                            class="mobile-ticket-action-bar"
                        >
                            <v-spacer />
                            <v-btn
                                class="ma-1 action-btn text-subtitle-2 filter-btn cancel-search"
                                :class="$vuetify.breakpoint.xs ? 'cancel-mobile-search filter-mobile-btn' : ''"
                                outlined
                                @click="showSearchFilter=false"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                class="ma-1 action-btn text-subtitle-2 filter-btn"
                                :class="$vuetify.breakpoint.xs ? 'filter-mobile-btn' : ''"
                                depressed
                                color="primary"
                                @click="applyTicketSearchFilter"
                            >
                                Search
                            </v-btn>
                            <v-spacer />
                        </v-toolbar>                       
                    </div>
                </div>
            </v-col>
            <v-col
                class="pt-0"
                :class="{'open-search-table-section': showSearchFilter}"
            >
                <v-row
                    class="mt-0"
                >
                    <!-- Search Bar -->
                    <v-col :class="this.$vuetify.breakpoint.mdAndUp ? 'py-0' : 'pt-0 px-7'">
                        <v-toolbar
                            flat
                            dense
                        >
                            <div
                                v-if="clipboardActive"
                                class="clipboard-controls"
                            >
                                <router-link
                                    to="/tickets/clipboard/routing"
                                    style="text-decoration: none; color: #1976D2"
                                >
                                    <v-btn
                                        outlined
                                        color="primary"
                                        class="text-capitalize"
                                        :width="this.$vuetify.breakpoint.mdAndUp ? '80px' : '40%'"
                                        @click="showSearchFilter = true"
                                    >
                                        <v-icon
                                            small
                                            class="material-icons-outlined"
                                        >
                                            map
                                        </v-icon>
                                        Route
                                    </v-btn>
                                </router-link>
                                <v-icon
                                    class="mx-2 custom-refresh-btn"
                                    @click="refreshTicketTab()"
                                >
                                    mdi-refresh
                                </v-icon>
                                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
                                <ClipboardItemPositionControls
                                    :tickets="tickets"
                                    :selected="selected"
                                    @move-clipboard-items="updateClipboardItems"
                                />
                            </div>
                            <v-btn
                                v-else-if="!showSearchFilter"
                                outlined
                                color="primary"
                                class="py-2 text-Caption text-capitalize"
                                :width="this.$vuetify.breakpoint.mdAndUp ? '160px' : '160px'"
                                @click="showSearchFilter = true"
                            >
                                <v-icon class="text-Caption">
                                    mdi-magnify
                                </v-icon>
                                Search Tickets
                            </v-btn>
                            <div v-if="!clipboardActive">
                                <Timer
                                    :key="timerKey"
                                    :refresh-minutes="autoRefreshMinutes"
                                    @refreshTicketTab="refreshTicketTab()"
                                />
                            </div>
                            <div class="filter-area">
                                <v-col
                                    v-if="Object.keys(filterChips).length && !clipboardActive && !this.$vuetify.breakpoint.xsOnly"
                                    class="filter-chips"
                                >
                                    <v-chip
                                        v-for="(value, name) in filterChips"
                                        :key="name"
                                        small
                                        class="ma-2"
                                        close
                                        @click:close.once="removeFilter(name)"
                                    >
                                        {{ name }}
                                    </v-chip>
                                    <v-chip
                                        color="white"
                                        class="ma-1"
                                        text-color="#1976D2"
                                        @click="clearFiltersAndApply"
                                    >
                                        Clear
                                    </v-chip>
                                </v-col>
                            </div>
                            <div>
                                <!-- Config Dialog -->
                                <TicketTableConfigDialog
                                    :dialog.sync="ticketPreferencesDialog"
                                    @syncTicketTable="syncTicketTable"
                                    @closeDialog="closeDialog"
                                    @defaultSort="defaultSort"
                                />
                            </div>                            
                            <div :no-gutters="this.$vuetify.breakpoint.xs">                                
                                <TicketDesktopHeaderToolbar
                                    v-if="this.$vuetify.breakpoint.mdAndUp"
                                    :selected-tickets="selected"
                                    :differences-disabled="differencesDisabled"
                                    :reopen-disabled="reopenDisabled"
                                    :respond-disabled="respondDisabled"
                                    :assign-disabled="assignDisabled"
                                    @open-compare-dialog="compare"
                                    @open-reopen-dialog="reopen"
                                    @open-respond-dialog="respond"
                                    @open-assign-dialog="assign"
                                />
                                <TicketsMobileHeaderToolbar
                                    v-else
                                />                                
                            </div>
                            <div>
                                <!-- Open Config Dialog -->
                                <v-btn
                                    v-if="this.$vuetify.breakpoint.mdAndUp"
                                    icon
                                    @click="dialogBtnClick"
                                >
                                    <v-icon>mdi-cog-outline</v-icon>
                                </v-btn>
                            </div>                           
                        </v-toolbar>
                    </v-col>
                </v-row>
                <v-row
                    v-if="Object.keys(filterChips).length && !clipboardActive && this.$vuetify.breakpoint.xsOnly"
                    class="filter-mobile-chips"
                    :style="{'margin-left': this.$vuetify.breakpoint.xsOnly ? '8px' : ''}"
                >
                    <v-chip
                        v-for="(value, name) in filterChips"
                        :key="name"
                        small
                        class="ma-2"
                        close
                        @click:close.once="removeFilter(name)"
                    >
                        {{ name }}
                    </v-chip>
                    <v-chip
                        color="white"
                        class="ma-1"
                        text-color="#1976D2"
                        @click="clearFiltersAndApply"
                    >
                        Clear
                    </v-chip>
                </v-row>
                <v-row
                    v-if="searchFilterApplied && !loading && tickets.length === 0"
                    align="center"
                    justify="center"
                    class="mt-0 no-data"
                >
                    <v-col
                        align="center"
                        justify="center"
                    >
                        <img src="../assets/search-icon.svg">
                        <span>No Results Found</span>
                        <span>Try adjusting your search filter to find what<br>you are looking for.</span>
                    </v-col>
                </v-row>
                <v-row
                    class="mt-0"
                    :no-gutters="this.$vuetify.breakpoint.xs"
                >
                    <v-col v-if="this.$vuetify.breakpoint.mdAndUp">
                        <v-data-table
                            v-show="!searchFilterApplied || (searchFilterApplied && tickets.length > 0) || loading"
                            id="main-tickets-table"
                            v-model="selected"
                            fixed-header
                            dense
                            :headers="headers"
                            :options.sync="paginationOptions"
                            :loading="loading"
                            :server-items-length="totalTicketsCount"
                            :items="tickets"
                            item-key="TR_rowid"
                            show-select
                            :height="`calc(100vh - ${clipboardActive ? 200 : 200}px)`"
                            :item-class="itemRowColor"
                            :footer-props="{
                                'items-per-page-options': perPageChoices,
                                height: '34px',
                                'margin-top': '0px',
                                'margin-bottom': '0px',
                            }"
                            :page.sync="page"
                            :hide-default-footer="hideFooter"
                            @toggle-select-all="selectAllToggle"
                        >
                            <template v-slot:[`header.clipboard`]>
                                <v-checkbox
                                    v-if="clipboardActive"
                                    v-model="clipboardAll"
                                    class="clipboard-checkbox"
                                    :on-icon="'bookmark'"
                                    :off-icon="'bookmark_border'"
                                    @click.native.prevent.stop.capture="clickClipboardAllCheckbox()"
                                />
                                <v-checkbox

                                    v-else
                                    v-model="clipboardAll"
                                    class="clipboard-checkbox"
                                    :on-icon="'bookmark'"
                                    :off-icon="'bookmark_border'"
                                />
                            </template>
                            <template v-slot:[`item.clipboard`]="{item}">
                                <v-checkbox
                                    v-if="clipboardActive"
                                    v-model="clipboardItems"
                                    class="clipboard-checkbox"
                                    :value="item"
                                    :value-comparator="setClipboardCheckState"
                                    :on-icon="'bookmark'"
                                    :off-icon="'bookmark_border'"
                                    :ripple="false"
                                    color="primary"
                                    @click.native.prevent.stop.capture="clickClipboardCheckbox($event, item)"
                                />
                                <v-checkbox
                                    v-else
                                    v-model="clipboardItems"
                                    class="clipboard-checkbox"
                                    :value="item"
                                    :value-comparator="setClipboardCheckState"
                                    :on-icon="'bookmark'"
                                    :off-icon="'bookmark_border'"
                                    :ripple="false"
                                    color="primary"
                                />
                            </template>
                            <template v-slot:[`item.TR_stage`]="{item}">
                                <v-chip
                                    x-small
                                    :color="statusChipColor(item.TR_stage)"
                                    outlined
                                    :class="statusChipClass(item.TR_stage, item.TS_cancelled)"
                                >
                                    {{ statusTextEx(item.TR_stage, item.TS_cancelled) }}
                                </v-chip>
                            </template>
                            <template v-slot:[`item.ticketRevision`]="{item}">
                                <router-link
                                    :class="ticketRevisionClass(item)"
                                    :to="'/tickets/details?tid='+guidNormal(item.tid)"
                                    style="color: #1976D2; white-space: nowrap;"
                                >
                                    {{ item.ticketRevision }}
                                </router-link>
                            </template>
                            <template v-slot:[`item.old_ticket`]="{item}">
                                <router-link
                                    :class="ticketRevisionClass(item)"
                                    :to="'/tickets/details?tid='+guidNormal(item.tid)"
                                    style="color: #1976D2; white-space: nowrap;"
                                >
                                    {{ item.old_ticket }}
                                </router-link>
                            </template>
                            <template v-slot:[`item.priority`]="{item}">
                                <v-icon
                                    small
                                    :color="priorityIconColor(item.priority)"
                                >
                                    {{ priorityIcon(item.priority) }}
                                </v-icon> {{ item.priority }}
                            </template>
                            <template v-slot:[`item.TR_insight_risk_score`]="{item}">
                                {{  riskScoreText(item.TR_insight_risk_score) }}
                            </template>
                            <template v-slot:[`item.TR_locator`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ locatorName(item.TR_locator_account) || 'Un-Assigned' }}
                                        </div>
                                    </template>
                                    <span>{{ locatorName(item.TR_locator_account) || 'Un-Assigned' }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.expires_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDate(item.expires_date) }}
                                </div>
                            </template>
                            <template v-slot:[`item.replace_by_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDate(item.replace_by_date) }}
                                </div>
                            </template>
                            <template v-slot:[`item.TR_seq_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDate(item.TR_seq_date) }}
                                </div>
                            </template>
                            <template v-slot:[`item.work_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.work_date) }}
                                </div>  
                            </template>
                            <template v-slot:[`item.work_end_date`]="{item}">
                                <div class="date-field">
                                    {{ item.work_end_date && item.work_end_date.length > 0 ? formatTicketDateTime(item.work_end_date) : '' }}
                                </div>
                            </template>                            
                            <template v-slot:[`item.dueDateText`]="{item}">
                                <div>
                                    {{ item.dueDateText }}
                                </div>
                            </template>
                            <template v-slot:[`item.duedate`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.duedate) }}
                                </div>
                            </template>
                            <template v-slot:[`item.location`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.location }}
                                        </div>
                                    </template>
                                    <span>{{ item.location }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.TS_updated`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.TS_updated) }}
                                </div>
                            </template>
                            <template v-slot:[`item.TR_response_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.TR_response_date) }}
                                </div>
                            </template>
                            <template v-slot:[`item.TR_updated`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.TR_updated) }}
                                </div>
                            </template>
                            <template v-slot:[`item.TS_viewed_last`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.TS_viewed_last) }}
                                </div>
                            </template>
                            <template v-slot:[`item.TS_posted`]="{item}">
                                <div style="white-space: nowrap;">
                                    {{ formatTicketDateTime(item.TS_posted) }}
                                </div>
                            </template>
                            <template v-slot:[`item.ticket_date`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.ticket_date) }}
                                </div>
                            </template>
                            <template v-slot:[`item.received`]="{item}">
                                <div class="date-field">
                                    {{ formatTicketDateTime(item.received) }}
                                </div>
                            </template>
                            <template v-slot:[`item.cross1`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.cross1 }}
                                        </div>
                                    </template>
                                    <span>{{ item.cross1 }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.cross2`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.cross2 }}
                                        </div>
                                    </template>
                                    <span>{{ item.cross2 }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.address_label`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.address_label }}
                                        </div>
                                    </template>
                                    <span>{{ item.address_label }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.excavator_reply_disposition`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.excavator_reply_disposition }}
                                        </div>
                                    </template>
                                    <span>{{ item.excavator_reply_disposition }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.work_type`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.work_type }}
                                        </div>
                                    </template>
                                    <span>{{ item.work_type }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.company`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.company }}
                                        </div>
                                    </template>
                                    <span>{{ item.company }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.place`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.place }}
                                        </div>
                                    </template>
                                    <span>{{ item.place }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.members`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.members }}
                                        </div>
                                    </template>
                                    <span>{{ item.members }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.full_address`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.full_address }}
                                        </div>
                                    </template>
                                    <span>{{ item.full_address }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.url`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.url }}
                                        </div>
                                    </template>
                                    <span>{{ item.url }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.street`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.street }}
                                        </div>
                                    </template>
                                    <span>{{ item.street }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.caller`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.caller }}
                                        </div>
                                    </template>
                                    <span>{{ item.caller }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.contact_name`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.contact_name }}
                                        </div>
                                    </template>
                                    <span>{{ item.contact_name }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:[`item.instructions`]="{item}">
                                <v-tooltip
                                    color="#34383f"
                                    top
                                    max-width="400"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            class="text-truncate"
                                            style="max-width: 140px"
                                            v-on="on"
                                        >
                                            {{ item.instructions }}
                                        </div>
                                    </template>
                                    <span>{{ item.instructions }}</span>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-col>
                    <v-col
                        v-else
                        class="pb-14"
                    >
                        <v-divider class="mx-4" />
                        <v-data-iterator
                            id="ticket-cards"
                            :items="tickets"
                            item-key="TR_rowid"
                            fixed-header
                            show-select
                            loading-text="Loading items..."
                            no-data-text=""
                            :server-items-length="totalTicketsCount"
                            :page.sync="page"
                            :options.sync="paginationOptions"
                            :loading="loading"
                            :footer-props="{
                                'items-per-page-options': perPageChoices
                            }"
                            :items-per-page="itemsPerPage"
                            @toggle-select-all="mobileSelectAll"
                        >
                            <template v-slot:header>
                                <TicketsMobileDataToolbar
                                    :display-toolbar="!!tickets.length"
                                    :selected-tickets="selected"
                                    @open-ticket-table-config="dialogBtnClick"
                                >
                                    <template v-slot:selectCheckbox>
                                        <v-checkbox
                                            v-model="mobileSelectAll"
                                        />
                                    </template>
                                    <template v-slot:clipboardCheckbox>
                                        <v-checkbox
                                            v-if="clipboardActive"
                                            v-model="clipboardAll"
                                            :on-icon="'bookmark'"
                                            :off-icon="'bookmark_border'"
                                            @click.native.prevent.stop.capture="clickClipboardAllCheckbox()"
                                        />
                                        <v-checkbox
                                            v-else
                                            v-model="clipboardAll"
                                            :on-icon="'bookmark'"
                                            :off-icon="'bookmark_border'"
                                        />
                                    </template>
                                </TicketsMobileDataToolbar>
                            </template>
                            <template v-slot:default="props">
                                <TicketsMobileCard
                                    v-for="item in props.items"
                                    :key="item.TR_rowid"
                                    :item="item"
                                    :item-left-border-color="itemRowColor(item)"
                                    :status-text="statusTextEx(item.TR_stage, item.TS_cancelled)"
                                    :status-chip-color="statusChipColor(item.TR_stage)"
                                    :status-chip-class="statusChipClass(item.TR_stage, item.TS_cancelled)"
                                    :due-date-text="dueDateText(item.duedate)"
                                    :priority-icon="priorityIcon(item.priority)"
                                    :priority-icon-color="priorityIconColor(item.priority)"
                                    :is-selected="!!selected.find(ticket => guidEqual(ticket.TR_rowid, item.TR_rowid))"
                                >
                                    <template v-slot:selectCheckbox>
                                        <v-checkbox
                                            v-model="selected"
                                            :value="item"
                                            :multiple="true"
                                            :value-comparator="setMobileCheckBoxState"
                                        />
                                    </template>
                                    <template v-slot:clipboardCheckbox>
                                        <v-checkbox
                                            v-if="clipboardActive"
                                            v-model="clipboardItems"
                                            :value="item"
                                            :value-comparator="setClipboardCheckState"
                                            :on-icon="'bookmark'"
                                            :off-icon="'bookmark_border'"
                                            color="primary"
                                            @click.native.prevent.stop.capture="clickClipboardCheckbox($event, item)"
                                        />
                                        <v-checkbox
                                            v-else
                                            v-model="clipboardItems"
                                            :value="item"
                                            :value-comparator="setClipboardCheckState"
                                            :on-icon="'bookmark'"
                                            :off-icon="'bookmark_border'"
                                            :ripple="false"
                                            color="primary"
                                        />
                                    </template>
                                </TicketsMobileCard>
                            </template>
                        </v-data-iterator>
                        <v-divider />
                        <v-toolbar
                            class="mobile-ticket-action-bar"
                        >
                            <v-spacer />
                            <v-btn
                                class="ma-1 action-btn text-subtitle-2"
                                depressed
                                color="primary"
                                :disabled="respondDisabled"
                                @click="respond"
                            >
                                <v-icon
                                    left
                                    class="action-icon"
                                >
                                    reply
                                </v-icon>
                                Respond
                            </v-btn>
                            <v-btn
                                class="ma-1 action-btn text-subtitle-2"
                                depressed
                                color="primary"
                                :disabled="assignDisabled"
                                @click="assign"
                            >
                                <v-icon
                                    left
                                    dark
                                    class="material-icons-outlined action-icon"
                                >
                                    assignment_turned_in
                                </v-icon>
                                Assign
                            </v-btn>
                            <v-spacer />
                        </v-toolbar>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-dialog
            v-model="dialog"
            width="400"
        >
            <v-card>
                <v-card-title class="text-subtitle-1 py-3 pl-5">
                    Add Ticket Tab
                </v-card-title>
                <v-divider />

                <v-card-text class="py-6 pl-5 pr-10">
                    <v-text-field
                        v-model="label"
                        label="Tab Label"
                        outlined
                        dense
                    />
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        outlined
                        color="indigo"
                        class="text-transform-none dialog-action-btn mr-2 text-capitalize"
                        @click="dialog = false"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        class="text-transform-none dialog-action-btn text-capitalize"
                        color="primary"
                        @click="addTicketTab"
                    >
                        Add
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="validateResponseDialog"
            max-width="400px"
        >
            <v-card id="ticket-selection-error-dialog">
                <v-card-title :class="{'ticket-section-error-mobile-title': this.$vuetify.breakpoint.xs}">
                    Ticket Selection Error
                </v-card-title>
                <v-divider />
                <v-card-text>
                    {{ validateResponseDialogText }}
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        @click="validateResponseDialog=false"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <TicketsReOpenDialog
            :selected-tickets="ticketsToBeReopened(selected)"
            :show="showTicketsReopenDialog"
            @close-reopen-dialog="closeReopen"
            @reopen-success="reopenSuccess"
        />
        <TicketsResponseDialog
            :selected-tickets="selected"
            :show="showTicketsResponseDialog"
            @close-response-dialog="closeRespond"
            @reponse-success="responseSuccess"
        />
        <TicketResponseErrorDialog
            :show="showRespondError"
            :respond-result="respondResult"
            @close-response-error-dialog="closeRespondErrorDialog"
        />
        <TicketsAssignDialog
            :selected-tickets="selected"
            :show="showTicketsAssignDialog"
            @close-assign-dialog="closeAssign"
            @assign-success="assignSuccess"
        />
        <v-dialog
            id="confirm-remove-clipboard-item"
            v-model="showConfirmRemoveClipboardItem"
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Remove Ticket
                </v-card-title>
                <v-divider />
                <v-card-text>
                    Remove 1 ticket from the Clipboard?
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        outlined
                        light
                        @click="cancelRemoveClipboardItem"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        @click="removeClipboardItem"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            id="confirm-remove-clipboard-items"
            v-model="showConfirmRemoveAllClipboardItems"
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Remove Tickets
                </v-card-title>
                <v-divider />
                <v-card-text>
                    Remove all tickets from the Clipboard?
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        outlined
                        light
                        @click="cancelRemoveAllClipboardItems"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        @click="removeAllClipboardItems"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            id="confirm-remove-tab"
            v-model="showConfirmRemoveTabModal"
            max-width="400px"
        >
            <v-card>
                <v-card-title class="text-h5 lighten-2">
                    <span>Delete</span>
                </v-card-title>
                <v-divider />
                <v-card-text v-if="selectedTabForDelete">
                    Are you sure you want to delete "{{ selectedTabForDelete.label }}" tab?
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        outlined
                        light
                        @click="closeRemoveTabModal"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        @click="removeTab(selectedTabForDelete.tabId)"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import TicketTableConfigDialog from '@/components/TicketTableConfigTabs/ConfigTabs.vue';
import { getUserOption, findUserOptionObjects } from '@/utils/options';
import { hasPermission } from '@/utils/permissions';
import { arraysAreSame } from '@/utils/compare';
import { riskScoreToText } from '@/utils/ticketRisk.js';
import { getTicketTypeColors } from '@/utils/ticketTypeColors';
import TicketDesktopHeaderToolbar from './TicketsDesktopHeaderToolbar';
import TicketsMobileHeaderToolbar from './TicketsMobileHeaderToolbar';
import TicketsMobileDataToolbar from './TicketsMobileDataToolbar';
import TicketsMobileCard from './TicketsMobileCard';
import TicketsReOpenDialog from './TicketsReOpenDialog.vue';
import TicketsResponseDialog from './TicketsResponseDialog.vue';
import TicketsAssignDialog from './TicketsAssignDialog.vue';
import {validateRespondSelection} from '@/services/ticketsResponse';
import {
    statusTextEx,
    isSType,
    fieldHeaderMap,
    formatTicketDate,
    formatTicketDateTime,
    defaultTicketFields,
    defaultTicketFilters,
    defaultOpenTicketsTabOptions,
    defaultClipboardTabOptions,
} from '../services/ticketDataHelpers';
import ClipboardItemPositionControls from './ClipboardItemPositionControls';
import TicketResponseErrorDialog from './ErrorDialogs/TicketRespondErrorDialog.vue';
import * as moment from 'moment';
import 'moment-duration-format';
import Timer from '@/components/Timer.vue';
import { guidEqual, guidNormal } from '@/utils/guid.js';
import _ from 'lodash';

export default {
    components: {
        TicketTableConfigDialog,
        TicketDesktopHeaderToolbar,
        TicketsMobileHeaderToolbar,
        TicketsMobileDataToolbar,
        TicketsMobileCard,
        TicketsReOpenDialog,
        TicketsResponseDialog,
        TicketsAssignDialog,
        ClipboardItemPositionControls,
        TicketResponseErrorDialog,
        Timer
    },
    data: () => ({
        orderBy: undefined, // default sort order on client side
        order: true,    // default sort direction on client side
        showClipboard: true,
        clipboardItemToRemove: undefined,
        showTicketsReopenDialog: false,
        showTicketsResponseDialog: false,
        showTicketsAssignDialog: false,
        showConfirmRemoveClipboardItem: false,
        showConfirmRemoveAllClipboardItems: false,
        text: 'table',
        isToggled: false,
        getTicketsFirstTime: true,
        selected: [],
        tickets: [],
        totalTicketsCount: 0,
        page: 1,
        itemsPerPage: 15,
        hideFooter: false,
        firstTicketShowing: 0,
        pageCount: 0,
        dialog: false,
        validateResponseDialog: false,
        validateResponseDialogText: '',
        label: null,
        showSearchFilter: false,
        tabModel: 0,
        ticketTabs: [],
        headers: [
            {
                text: 'test',
                value: 'clipboard',
                width: '30px',
                sortable: false,
            },            
            {
                text: 'Ticket',
                value: 'ticketRevision',
                width: '156px'
            },            
        ],
        ticketColorCoding: [
            {
                type: 'cancel',
                class: 'brown-lighten-1',
            },
            {
                type: 'damage',
                class: 'purple-darken-1',
            },
            {
                type: 'overdue',
                class: 'red-darken-1',
            },
            {
                type: 'due4',
                class: 'orange-dark',
            },
            {
                type: 'due8',
                class: 'red-lighten-4',
            },
            {
                type: 'short_notice',
                class: 'yellow-darken-1',
            },
            {
                type: 'late',
                class: 'amber-darken-2',
            },
            {
                type: 'update',
                class: 'light-green-darken-1',
            },
        ],
        perPageChoices: [15, 25, 50, 100, 250, 500],
        // Show or Hide Ticket Table Config Dialog Nested Component
        ticketPreferencesDialog: false,
        dues: [
            {text:'Any',value:'*'},
            {text:'Overdue',value:'OVERDUE'},
            {text:'Yesterday',value:'YESTERDAY'},
            {text:'Today', value:'TODAY'},
            {text:'Tomorrow',value:'TOMORROW'},
            {text:'<= 2 Hrs',value:'<2'},
            {text:'<= 4 Hrs',value:'<4'},
            {text:'<= 6 Hrs', value:'<6'},
            {text:'<= 8 Hrs',value:'<8'},
            {text:'<= 10 Hrs', value:'<10'},
            {text:'<= 12 Hrs', value:'<12'},
            {text:'<= 24 Hrs', value:'<24'},
            {text:'<= 36 Hrs', value:'<36'},
            {text:'<= 48 Hrs', value:'<48'},
        ],
        stages: [
            {text:'Open', value:'*'},
            {text:'Closed', value:'CT'},
            {text:'Un-Assigned', value:'IR'},
            {text:'Assigned', value:'AL'},
            {text:'Open + Closed', value:'ALL'},
        ],
        seqDateMenu: null,
        seqDate: null,
        workDate: null,
        workDateMenu: null,
        updated: null,
        updatedMenu: null,
        priority: 'Any',
        type: 'Any',
        category: 'Any',
        locator: 'Any',
        due: 'Any',
        stage: 'Open',
        downloadDialog: false,
        downloadFormat: 'text',
        PrintTicketsDialog: false,
        searchFilter:{
            TicketNumber: '',
            Comment: '',
            Center: '',
            SeqDate: '',
            WorkDate: '',
            UpdateDate: '',
            MemberCode: '',
            Stage: '',
            Priority: '',
            TicketType: '',
            Category: '',
            Counties: '',
            Locator: '',
            Due: '',
            Grid: '',
            Place: '',
            Subdivision: '',
            Address: '',
            Street: '',
            Cross: '',
            Company: '',
            Caller: '',
            DoneFor: '',
            Location: '',
            WorkType: '',
            Risk: ''
        },
        filterChips: {},
        searchFilterApplied: false,
        userConfigColors: getTicketTypeColors(),
        respondResult: {},
        showRespondError: false,
        paginationOptions: {
            itemsPerPage: 100,
            page: 1,
        },
        loading: false,
        autoRefreshMinutes: 0,
        timerKey: 0,
        showConfirmRemoveTabModal: false,
        selectedTabForDelete: null,
    }),
    computed: {
        useInsightLogixRisk() {
            return hasPermission('USE_INSIGHT_LOGIX_RISK');
        },
        formatedCountdown() {
            return moment.duration(this.refreshCountdown, 'seconds').format('m:ss');
        },
        clipboardActive() {
            return this.activeTab.tabId === 'tickets-clipboard';
        },
        clipboardItems: {
            get() {
                return this.$store.getters.userClipboard;
            },
            async set(newValue) {
                if(newValue === null) {
                    newValue = [];
                }
                const temp = this.$store.getters.userClipboard;
                let oldRowIds=[];
                if (Array.isArray(temp)){
                    oldRowIds = temp.map(item => item.TR_rowid);
                }else{
                    oldRowIds.push(temp.TR_rowid);
                }
                let newRowIds=[];
                if (Array.isArray(newValue)){
                    newRowIds = newValue.map(item => item.TR_rowid);
                }else{
                    newRowIds.push(newValue.TR_rowid);
                }

                let update = {};
                if(oldRowIds.length === 0) {
                    if(newRowIds.length) {
                        // add all
                        update.add = newRowIds.join();
                    }
                } else if (newRowIds.length === 0 ){
                    // remove all
                    update.remove = oldRowIds.join();
                } else {
                    // get new items not in old
                    update.add = newRowIds.filter(rowid => !oldRowIds.includes(rowid));
                    if (update.add.length===0) update.add = undefined;
                    // get old items not in new
                    update.remove = oldRowIds.filter(rowid => !newRowIds.includes(rowid));
                    if (update.remove.length===0) update.remove = undefined;
                }
                await this.$store.dispatch('updateUserClipboard', update);
                if(update.remove && this.clipboardActive){
                    this.getClipBoardTickets();
                }
            }
        },
        clipboardItemsNotInTickets() {
            return this.clipboardItems.filter(item => {
                if(!this.tickets.find(ticket => guidEqual(item.TR_rowid, ticket.TR_rowid))) {
                    return item;
                }
            });
        },
        clipboardAll: {
            set(newValue) {
                if(newValue) {
                    this.clipboardItems = [...this.clipboardItemsNotInTickets, ...this.tickets];
                } else {
                    this.clipboardItems = this.clipboardItemsNotInTickets;
                }
            },
            get() {
                if(this.tickets.length === 0 || this.clipboardItems.length === 0) {
                    return false;
                }

                let allInClipboard = true;
                for (const ticket of this.tickets){
                    let inClipBoard = false;
                    for (const clipboardItem of this.clipboardItems){
                        if (guidEqual(ticket.TR_rowid, clipboardItem.TR_rowid)){
                            inClipBoard = true;
                            break;
                        }
                    }
                    if (!inClipBoard){
                        allInClipboard = false;
                        break;
                    }
                }
                return allInClipboard;
            },
        },
        mobileSelectAll: {
            set(newValue) {
                this.selected = newValue ? [...this.tickets] : [];
            },
            get() {
                const ticketCount = this.tickets.length;
                return ticketCount > 0 && ticketCount === this.selected.length;
            },
        },
        companyName() {
            return this.$store.getters.company.company;
        },
        activeTab: {
            set(newValue) {
                this.$store.commit('SET_ACTIVE_TAB', newValue);
            },
            get(){
                return this.$store.getters.activeTab;
            }
        },
        differencesDisabled(){
            return this.selected.length !== 2;
        },
        shareDisabled(){
            return this.selected.length === 0;
        },
        printDisabled(){
            return this.selected.length === 0;
        },
        reopenDisabled(){
            return this.selected.length === 0;
        },
        respondDisabled(){
            return this.selected.length === 0;
        },
        assignDisabled(){
            return this.selected.length === 0;
        },
        activeTicketTab() {
            return this.ticketTabs[this.tabModel];
        },
        getActiveTabObject() {
            const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
            let optJSONTAB = getUserOption(JSONTAB);
            if(!optJSONTAB || !optJSONTAB.length) {
                // create a default
                if(this.activeTab.tabId === 'tickets-clipboard') {
                    let tab = { ...defaultClipboardTabOptions };
                    tab.config.fields = getUserOption('CLIPBOARD_FIELDS');
                    optJSONTAB = JSON.stringify(tab);
                } else {
                    optJSONTAB = JSON.stringify(defaultOpenTicketsTabOptions);
                }
            }
            let tabJson = JSON.parse(optJSONTAB);
            return tabJson; 
        },
        lastTicketShowing() {
            const pagedCount = (this.firstTicketShowing-1) + this.itemsPerPage;
            if(pagedCount <= this.totalTicketsCount) {
                return pagedCount;
            } else {
                return ((this.pageCount - 1) * this.itemsPerPage) + this.totalTicketsCount % this.itemsPerPage;
            }
        },
        showingTicketsText() {
            return `Showing ${this.firstTicketShowing} to ${this.lastTicketShowing} of ${this.totalTicketsCount} Results`;
        },
        companyUsingSingleCenter() {
            let companyCenters = this.$store.getters.companyCenters;
            return companyCenters.length === 1;
        },
        companyUsingSingleState() {
            let companyCenters = this.$store.getters.companyCenters;
            return companyCenters.length === 1 && companyCenters[0].__STATES.length === 1;
        },
        getCompanyCenters() {
            return this.$store.getters.companyCenters;
        },
        companyMemberCodes() {
            return this.$store.getters.companyMemberCodes;
        },
        counties() {
            return this.$store.getters.counties.map((item) => { return { name: `${item[3]}, ${item[2]}`, value: item [1] }; });
        },
        companyTicketPriorities() {
            let companyTicketPriorities  = [];
            // Add the default priority
            companyTicketPriorities.push({abbrev: '*',text: 'Any'});
            // Add the company priorities
            for(let priority of this.$store.getters.companyTicketPriorities) {
                companyTicketPriorities.push({abbrev: priority.abbrev, text: priority.abbrev});
            }
            return companyTicketPriorities;
        },
        riskOptions() {
            let riskTypes = [];
            // Add the Any risk type
            riskTypes.push({value: '*',text: 'Any'});
            // Add the risk types
            for(let riskType of this.$store.getters.riskOptions) {
                riskTypes.push({value: riskType, text: riskType});
            }
            return riskTypes;
        },
        companyLocators() {
            let companyLocators= [];
            // Add Any and Un-Assigned Locators
            companyLocators.push({value: '*',name: 'Any'});
            companyLocators.push({value: '{00000000-0000-0000-0000-000000000000}',name: 'Un-Assigned'});
            // Add Company Locators
            let data = this.$store.getters.companyUsers;
            let locators = data.filter(item => { return item.ingroup.includes('Locator');});
            for(let locator of locators) {
                let locatorName = `${locator.account} : ${locator.name}`;
                let locatorValue = locator.uid;
                companyLocators.push({ value: locatorValue, name: locatorName });
            }
            return companyLocators;
        },
        companyCounties(){
            let companyCounties= [];
            let counties = this.counties;
            for(let county  of counties) {
                companyCounties.push(county);
            }
            return companyCounties;
        },
        companyCenters() {
            let companyCenters= [];
            companyCenters.push({value: '*',name: 'Any'});
            let centers = this.getCompanyCenters;
            for(let center  of centers) {
                companyCenters.push({value: center.center_id.toString(), name: center.center_abbrev});
            }
            return companyCenters;
        },
        companyTicketTypes() {
            let companyTicketTypes = [];
            // Add the any ticket type
            companyTicketTypes.push({abbrev: '*',text: 'Any'});
            // Add the ticket types from the company
            for(let ticketType of this.$store.getters.companyTicketTypes) {
                companyTicketTypes.push({abbrev: ticketType.abbrev, text: ticketType.abbrev});
            }
            return companyTicketTypes;
        },
        companyTicketCategories() {
            let companyTicketCategories = [];
            // Add the any ticket type
            companyTicketCategories.push({abbrev: '*',text: 'Any'});
            // Add the ticket types from the company
            for(let category of this.$store.getters.companyTicketCategories) {
                companyTicketCategories.push({abbrev: category.abbrev, text: category.abbrev});
            }
            return companyTicketCategories;
        },
        SeqDateText () {
            return this.searchFilter.SeqDate !== '' ? this.searchFilter.SeqDate !== '*' ? this.searchFilter.SeqDate.join(':') : '' : '';
        },
        WorkDateText () {
            return this.searchFilter.WorkDate !== '' ? this.searchFilter.WorkDate !== '*' ? this.searchFilter.WorkDate.join(':') : '' : '';
        },
        UpdateDateText () {
            return this.searchFilter.UpdateDate !== '' ? this.searchFilter.UpdateDate !== '*' ? this.searchFilter.UpdateDate.join(':') : '' : '';
        },
        companyUsesSingleCenter() {
            return this.getCompanyCenters.length === 1;
        },
        companyUsesSingleMemberCode() {
            return this.companyMemberCodes.length === 1;
        },
        selectedAllCenters () {
            return this.searchFilter.Center.length === this.getCompanyCenters.length;
        },
        selectedSomeCenters () {
            return this.searchFilter.Center.length > 0 && !this.selectedAllCenters;
        },
        selectCentersIcon () {
            if (this.selectedAllCenters) return 'mdi-close-box';
            if (this.selectedSomeCenters) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        selectedAllMemberCode () {
            return this.searchFilter.MemberCode.length === this.companyMemberCodes.length;
        },
        selectedSomeMemberCode () {
            return this.searchFilter.MemberCode.length > 0 && !this.selectedAllMemberCode;
        },
        selectMemberCodeIcon () {
            if (this.selectedAllMemberCode) return 'mdi-close-box';
            if (this.selectedSomeMemberCode) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
    },
    watch: {
        selected(newValue, oldValue) {
            if(!arraysAreSame(newValue, oldValue)) {
                this.storeTabSelectedItems();
            }
        },
        paginationOptions: {
            async handler (newValue, oldValue) {              
                // Vuetify Datatable use sortDesc and sortBy, but we use orderBy and order
                if(newValue.sortBy.length > 0) {
                    this.orderBy = newValue.sortBy[0];
                }else{
                    // Set the default sort order when the sort order is cleared
                    const options = this.getActiveTabObject;                        
                    if (options.config?.orderBy){
                        if (options.config.orderBy.includes('-')) {                                
                            const orderByArray = options.config.orderBy.split('-');
                            this.orderBy = orderByArray[0];
                            this.order = orderByArray[1];
                        }else{
                            this.orderBy = 'ticket';  
                        }
                    }else{
                        this.orderBy = 'ticket';
                    }
                }
                if(newValue.sortDesc !== oldValue.sortDesc && oldValue.sortDesc && newValue.sortDesc.length > 0) {                
                    this.order = newValue.sortDesc[0] === false ? 'asc' : 'desc';
                }
                if(newValue.itemsPerPage !== oldValue.itemsPerPage) {
                    await this.updateItemsPerPage();
                }
                if(newValue.page !== oldValue.page) {
                    this.page = newValue.page;
                    await this.updatePage();
                }
                this.activeTab.tabId === 'tickets-clipboard' ? this.refreshTicketTab() : this.getTickets();
            },
            // deep: true,
        },
    },
  
    async mounted() {
        // this.otherElementHeight = document.querySelector('#other-element').clientHeight;
        await this.$store.dispatch('loadTicketPageData');
        this.ticketTabs = this.getTicketTabs();
        this.setTicketTabCounts();
        if(!this.activeTab.tabId) {
            this.selectedTab('open-tickets','Tickets');
        }else{
            this.selectedTab(this.activeTab.tabId, this.activeTab.label);
        }
    },
    destroyed(){
        // Reset the ticket page numbers
        this.resetPages();
    },
    methods: {
        // local method that implements the imported method
        guidEqual(guid1, guid2) {
            return guidEqual(guid1, guid2);
        },
        guidNormal(guid) {
            return guidNormal(guid);
        },
        forceRerender() {
            this.timerKey += 1;
        },
        selectAllCenter() {
            if(this.selectedAllCenters) {
                this.searchFilter.Center = [];
            } else {
                this.searchFilter.Center = this.getCompanyCenters.map((center) => center.center_id);
            }
        },
        selectAllMemberCode() {
            if(this.selectedAllMemberCode) {
                this.searchFilter.MemberCode = [];
            } else {
                this.searchFilter.MemberCode = this.companyMemberCodes.map((member) => member.member_code);
            }
        },
        clearSelectedCounties() {
            this.searchFilter.Counties = [];
        },
        changeCounties(change) {
            if(change.length > 1) {
                if(change.length > 10) {
                    this.$store.dispatch('setSnackbar', { color:'error', text: 'You can select a maximum of 10 counties', timeout:3000 });
                    this.searchFilter.Counties.pop();
                } else {
                    this.searchFilter.Counties = change.filter(item => (item !== '*'));
                }
            }
        },
        closeRespondErrorDialog() {
            this.showRespondError = false;
            this.respondResult = {};
        },
        initColors() {
            this.userConfigColors = getTicketTypeColors();
        },
        updateClipboardItems(newList) {
            this.tickets = newList;
            this.$store.dispatch('setUserClipboardOrder', newList.map(ticket => ticket.TR_rowid).join());
        },
        cancelRemoveClipboardItem() {
            this.clipboardItemToRemove = undefined;
            this.showConfirmRemoveClipboardItem = false;
        },
        cancelRemoveAllClipboardItems() {
            this.showConfirmRemoveAllClipboardItems = false;
        },
        removeClipboardItem() {
            if(this.selected?.length){
                this.selected = this.selected.filter(item => !guidEqual(item.TR_rowid, this.clipboardItemToRemove.TR_rowid));
            }
            this.clipboardItems = this.clipboardItems.filter(item => !guidEqual(item.TR_rowid, this.clipboardItemToRemove.TR_rowid));
            this.clipboardItemToRemove = undefined;
            this.showConfirmRemoveClipboardItem = false;
        },
        removeAllClipboardItems() {
            this.selected = [];
            this.clipboardItems = [];
            this.showConfirmRemoveAllClipboardItems = false;
        },
        clickClipboardCheckbox(theEvent, item) {            
            if(this.clipboardItems.find(ticket => guidEqual(ticket.TR_rowid, item.TR_rowid))) {
                this.clipboardItemToRemove = item;
                this.showConfirmRemoveClipboardItem = true;
            }
        },
        clickClipboardAllCheckbox() {
            this.showConfirmRemoveAllClipboardItems = true;
        },
        moveArrayItem (targetArray, indexFrom, indexTo) {
            let targetElement = targetArray[indexFrom];
            let increment = (indexTo - indexFrom) / Math.abs (indexTo - indexFrom);

            for (let Element = indexFrom; Element != indexTo; Element += increment){
                targetArray[Element] = targetArray[Element + increment];
            }
            targetArray[indexTo] = targetElement;
        },
        changeClipboardItemPosition(newPosition) {
            let newTickets = [...this.tickets];
            let changed = false;
            let selected = this.selected;
            let lastIndex = newTickets.length-1;
            let selectedCount = selected.length;
            let indexFrom = 0;
            let foundCount = 0;
            // need to move selected tickets based on the order inwhich they occur in newTickets
            // If moving down start with latst item and iterate backwards else start at position zero and iterate forwards
            for(indexFrom = newPosition === 1 ? lastIndex : 0; indexFrom > -1 && indexFrom < newTickets.length; indexFrom -= newPosition) {
                let ticket = newTickets[indexFrom];
                if(selected.find(selectedTicket => guidEqual(selectedTicket.TR_rowid, ticket.TR_rowid))) {
                    ++foundCount;
                    if((newPosition === -1 && indexFrom > 0)
                    || (newPosition ===  1 && indexFrom < lastIndex)) {
                        this.moveArrayItem(newTickets, indexFrom, indexFrom+newPosition);
                        changed = true;
                    }
                }
                if(foundCount === selectedCount) {
                    break;
                }
            }
            // Only post to server if something actually changed.
            if(changed) {
                this.tickets = newTickets;
                this.$store.dispatch('setUserClipboardOrder', newTickets.map(ticket => ticket.TR_rowid).join());
            }
        },
        setClipboardCheckState(model, item) {
            return guidEqual(model?.TR_rowid, item?.TR_rowid);
        },
        setMobileCheckBoxState(model, item) {
            return guidEqual(model?.TR_rowid, item?.TR_rowid);
        },
        setTicketTabCounts() {
            try{
                for(let tab of this.ticketTabs) {
                // exclude clipboard and active tab
                    if(!tab.isClipboard && tab.tabId !== this.activeTab.tabId) {
                        webtmsBackend.getTicketCount(tab.config.filters)
                            .then(result => {
                                let temp = [...this.ticketTabs];
                                for(let ticketTab of temp) {
                                    if(ticketTab.tabId === tab.tabId) {
                                        ticketTab.ticketCount = result.criteria.pageInfo.rowCount;
                                        break;
                                    }
                                }
                                this.ticketTabs = temp;
                            });
                    }
                }
            } catch (e) {
                throw e.err;
            }
        },
        getTicketTabs() {
            const existingTabs = [...this.ticketTabs];
            let openTicketTab = getUserOption('JSON:TAB:open-tickets');
            if(openTicketTab) {
                openTicketTab = JSON.parse(openTicketTab);
            } else {
                openTicketTab = defaultOpenTicketsTabOptions;
            }
            // get the clipboard tab
            let clipboardTab = getUserOption('JSON:TAB:tickets-clipboard');
            let clipboardFields = getUserOption('CLIPBOARD_FIELDS');
            if(clipboardTab) {
                clipboardTab = JSON.parse(clipboardTab);
                clipboardTab.config = {};
                clipboardTab.config.fields = clipboardFields;
            } else {
                clipboardTab = defaultClipboardTabOptions;
            }
            clipboardTab.isClipboard = true;
            let userTabs = findUserOptionObjects('JSON:TAB:open-tickets--')
                .map(optionObject => {
                    let parsed = JSON.parse(optionObject.value);
                    let id = optionObject.option.replace('JSON:TAB:', '');
                    if(parsed.tabId !== id) {
                        parsed.tabId = id;
                        parsed.tabMoniker = id.replace('JSON:TAB:open-tickets--', '');
                    }
                    parsed.removeable = true;
                    return parsed;
                })
                .sort((a, b) => Number(a.tabMoniker) - Number(b.tabMoniker));

            let returnTabs = [];
            if(clipboardTab) {
                returnTabs = [openTicketTab, ...userTabs, clipboardTab];
            } else {
                returnTabs = [openTicketTab, ...userTabs];
            }
            for(let tab of existingTabs) {
                if(!tab.isClipboard) {
                    let foundTab = returnTabs.find(returnTab => returnTab.tabId === tab.tabId);
                    if(foundTab) {
                        foundTab.ticketCount = tab.ticketCount;
                    }
                }
            }
            return returnTabs;
        },
        storeTabSelectedItems() {
            const tabSelectedItems = this.$store.getters.tabSelectedItems;
            tabSelectedItems[this.activeTab.tabId] = [...this.selected];
            this.$store.commit('SET_TAB_SELECTED_ITEMS', tabSelectedItems);
        },
        compare() {
            console.log('open compare dialog');
        },
        reopen() {
            this.showTicketsReopenDialog = true;
        },
        closeReopen() {
            this.showTicketsReopenDialog = false;
        },
        reopenSuccess() {
            // this.updateTickets(result);
            this.getTickets();           
            this.closeReopen();
        },
        respond() {
            let errorText = validateRespondSelection(
                this.selected,
                this.$store.getters.companyResponseCodes,
                isSType);
            if(errorText) {
                this.validateResponseDialogText = errorText;
                this.validateResponseDialog = true;
            } else {
                this.showTicketsResponseDialog = true;
            }
        },
        closeRespond() {
            this.showTicketsResponseDialog = false;
            this.respondResult = {};
        },
        responseSuccess(result) {
            this.updateTickets(result);
            this.closeRespond();
            if(result.errors) {
                this.respondResult = result;
                this.showRespondError = true;
            }
        },
        updateTickets(result) {
            let changed = false;
            let newTickets = [...this.tickets];
            for(let ticket of result.tickets) {
                if(ticket.result.result === 'OK') {
                    let index = newTickets.findIndex(ticky => guidEqual(ticky.TR_rowid, ticket.datarow.TR_rowid));
                    if(index !== -1){
                        Object.assign(newTickets[index], this.formatTicketForTable(ticket.datarow));
                        changed = true;
                    } else {
                        console.error('could not find ', ticket.datarow.TR_rowid);
                    }
                }
            }
            if(changed) {
                this.tickets = newTickets;
                const tabSelectedItems = this.$store.getters.tabSelectedItems;
                delete tabSelectedItems[this.activeTab.tabId];
                this.$store.commit('SET_TAB_SELECTED_ITEMS', tabSelectedItems);
                this.selected = [];
            }
        },
        assign() {
            this.showTicketsAssignDialog = true;
        },
        closeAssign() {
            this.showTicketsAssignDialog = false;
        },
        assignSuccess(result) {
            this.updateTickets(result);
            this.closeAssign();
        },
        loadingTickets() {

        },
        customSort() {

        },
        dueDateText(duedate) {
            const now = new Date();
            if (duedate) {
                const d = Date.parse(duedate);
                if (d <= now) {
                    return 'Overdue';
                }
                const diff = d - now;
                const hr = 1 + Math.floor(diff / 3600000);
                return '<' + hr + 'Hr';
            }
            return '?UNKNOWN';
        },
        ticketRevisionClass(item) {
            if(item.TS_cancelled) {
                return `status-cancelled-ticketrevision`;
            }
            return 'status-open-ticketrevision';
        },
        statusChipClass(stage,cancelled) {
            return `status-${statusTextEx(stage,cancelled).toLowerCase()}-chip`;
        },
        statusChipColor(stage) {
            return stage === 'CT' ? '#FFB300' : 'green';
        },
        statusTextEx(stage,cancelled) {
            return statusTextEx(stage,cancelled);
        },
        priorityIconColor(priority) {
            switch(priority) {
                case 'NORM':
                    return 'grey';
                case 'RUSH':
                    return '#fdd835';
                case 'EMER':
                    return 'red';
                case 'NONC':
                    return 'light-blue';
                default:
                    return '';
            }
        },
        priorityIcon(priority) {
            switch(priority) {
                case 'NORM':
                    // gray circle exclamation
                    return 'error';
                case 'RUSH':
                    // yellow circle exclamation
                    return 'error';
                case 'EMER':
                    // red bell exclamation
                    return 'notification_important';
                case 'NONC':
                    // light blue circle exclamation
                    return 'error';
                default:
                    return '';
            }
        },
        riskScoreText(risk) {
            return riskScoreToText(risk);
        },
        changeItemsPerPage() {
            // this.updateItemsPerPage();
        },
        formatTicketDate(dateString) {
            return formatTicketDate(dateString);
        },
        formatTicketDateTime(dateString) {
            return formatTicketDateTime(dateString);
        },
        formatTicketForTable(ticket) {
            ticket.dueDateText = this.dueDateText(ticket.duedate);
            ticket.ticketRevision = ticket.ticket;
            if(ticket.revision) {
                ticket.ticketRevision += '-' + ticket.revision;
            }
            return ticket;
        },
        async getTickets() {
            if (this.getTicketsFirstTime) {
                this.getTicketsFirstTime = false;
                return;
            }
            this.loading = true;
            this.hideFooter = false;
            this.tickets = [];
            this.totalTicketsCount = 0;           
            try {
                const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
                let options = [];
                options.push([JSONTAB, JSON.stringify(this.getActiveTabObject)]);
                // Update this.headers based on ticket tab fields
                this.syncTicketTableHeader();
                let optionValue = options[0][1];
                if(optionValue) {                    
                    optionValue = JSON.parse(optionValue);
                    this.autoRefreshMinutes = optionValue.config.autoRefreshMinutes ? optionValue.config.autoRefreshMinutes : 0;                    
                    this.orderBy = this.orderBy ? this.orderBy : optionValue.config.orderBy;
                    this.fields = optionValue.config.fields ? optionValue.config.fields : defaultTicketFields;
                    this.itemsPerPage = optionValue.config?.pageSize ? optionValue.config.pageSize : 25;      
                                } else {
                    this.orderBy = 'ticket';
                    this.order = true;                    
                }
                this.defaultSort(this.orderBy);

                // if (this.page) {
                //     this.page = page;
                // }

                // if (itemsPerPage) {
                //     this.itemsPerPage = itemsPerPage;
                // }

                // update the store with the config options for the active tab
                // this.$store.dispatch('setConfigOrderByDefault', this.orderBy);
                // this.$store.dispatch('setConfigRefreshMinutes', this.autoRefreshMinutes);
                // this.$store.dispatch('setConfigFields', this.fields);
                this.$store.dispatch('setItemsPerPage', this.itemsPerPage);

                // reloads the Timer Component with the new autoRefreshMinutes;
                this.timerKey += 1;

                // Passing in orderBy as the orderBy parameter to support server side sorting
                const filters = optionValue?.config?.filters ? optionValue.config.filters : defaultTicketFilters;
                                if(filters){
                    this.setSelectedSearchFilter(filters);
                                    }  

                let ticketResponse = await webtmsBackend.getTickets(this.page, this.itemsPerPage, this.fields, `${this.orderBy}-${this.order}`, filters);
                this.totalTicketsCount = ticketResponse.criteria.pageInfo.rowCount;
                if (this.activeTicketTab) this.activeTicketTab.ticketCount = ticketResponse.criteria.pageInfo.rowCount;
                this.firstTicketShowing = ticketResponse.criteria.pageInfo.rowStart;
                this.pageCount = ticketResponse.criteria.pageInfo.pageCount;
                const tickets = this.mergeTickets(this.selected, ticketResponse.data,this.orderBy,this.order );

                this.tickets = tickets.map(ticket => {
                    this.formatTicketForTable(ticket);
                    return ticket;
                });

                this.setSearchFilterApplied();
                this.initColors();
                this.loading = false;
            } catch(error) {
                console.error('getTickets error ', error);
                this.loading = false;
            }
        },
        /**
         * Merges the selected tickets with the ticket response data and removes duplicates.
         * @param {Array} selected - The array of selected tickets.
         * @param {Object} ticketResponse - The response data from the ticket API.
         * @returns {Array} The array of unique tickets.
         */
        mergeTickets(selectedTickets, ticketResponse, orderBy, order) {
            if(selectedTickets.length > 1 ) {  
                selectedTickets.sort(function(a, b) {
                    let aValue = isNaN(Date.parse(a[orderBy])) ? a[orderBy] : Date.parse(a[orderBy]);
                    let bValue = isNaN(Date.parse(b[orderBy])) ? b[orderBy] : Date.parse(b[orderBy]);
                    return order === 'asc' ? aValue - bValue : bValue - aValue;
                });
            }
            const tickets = _.uniqBy([...selectedTickets, ...ticketResponse], 'TR_rowid');
            return tickets;
        },
        itemRowColor(ticket) {
            for(let colorCode of this.userConfigColors) {
                if(colorCode.selected && isSType(colorCode.type, ticket)) {
                    return colorCode.class;
                }
            }
        },
        dialogBtnClick(){
            // Show Ticket Table Config Dialog Nested Component
            this.ticketPreferencesDialog = true;
        },
        closeDialog(){
            // Hide Ticket Table Config Dialog Nested Component
            this.ticketPreferencesDialog=false;
            // this.forceRerender();
            this.timerKey += 1;
        },
        getUserOptions: async function() {
            let response = await webtmsBackend.getUserOptions();
            this.$store.commit('SET_USER_OPTIONS', response.userOptions);
            this.$store.commit('SET_OVER_VIEW_OPEN_TICKETS', response.overviewOpenTickets);
        },
        async removeTab(tabId) {            
            const tabIndex = this.ticketTabs.findIndex(tab => tab.tabId === tabId);
            const activeTab = this.activeTab.tabId;
            const deletingOtherTab = tabId !== activeTab;
            await webtmsBackend.deleteUserOption('JSON:TAB:'+ tabId);
            this.closeRemoveTabModal();
            await this.getUserOptions();
            this.ticketTabs = this.getTicketTabs();
            if(deletingOtherTab) {
                const tab = this.ticketTabs.find(tab => tab.tabId === activeTab);
                if(tab) {
                    this.selectedTab(tab.tabId, tab.label);
                } else {
                    this.selectedTab('open-tickets','Tickets');
                }
            } else {
                if(tabIndex > 0) {
                    const tab = this.ticketTabs[tabIndex-1];
                    if(tab) {
                        this.selectedTab(tab.tabId, tab.label);
                    } else {
                        this.selectedTab('open-tickets','Tickets');
                    }
                } else {
                    this.selectedTab('open-tickets','Tickets');
                }
            }
            const tabSelectedItems = this.$store.getters.tabSelectedItems;
            delete tabSelectedItems[tabId];
            this.$store.commit('SET_TAB_SELECTED_ITEMS', tabSelectedItems);

        },
        handelOpenAddTicketTabModel() {
            this.dialog = true;
            // 2 because opent-tickets and clipboard ever present
            if(this.ticketTabs.length > 2) {
                // -2 because clipboard always last tab
                if(this.ticketTabs[this.ticketTabs.length - 2]) {
                    this.label = this.ticketTabs[this.ticketTabs.length - 2].label;
                }
            }
        },
        async addTicketTab() {
            const result = await webtmsBackend.addNewTicketTab({ label: this.label });
            if(result.failed.length) {
                console.error('addTicketTab failed with ', result.failed);
            } else {
                let newTab = JSON.parse(result.set[0][1]);
                await this.getUserOptions();
                this.ticketTabs = this.getTicketTabs();
                newTab = this.ticketTabs.find(tab => tab.tabId === newTab.tabId);
                if(newTab) {
                    this.selectedTab(newTab.tabId, newTab.label);
                } else {
                    this.selectedTab('open-tickets','Tickets');
                }
            }

            this.label=null;
            this.dialog=false;
        },
        async selectedTab (id, label, loadTickets=true) {
            const tabIndex = this.ticketTabs.findIndex(tab => tab.tabId === id);
            if(tabIndex === -1) {
                this.tabModel = 0;
                let defaultTab = this.ticketTabs[this.tabModel];
                if (typeof defaultTab !=='undefined'){
                    this.activeTab = {
                        tabId: defaultTab.tabId,
                        label: defaultTab.label
                    };
                }
            } else {
                this.activeTab = {tabId: id, label: label};
                this.tabModel = tabIndex;
            }

            if (id === 'tickets-clipboard') {
                if (this.paginationOptions.itemsPerPage !== this.getActiveTabObject?.config?.pageSize){
                    this.paginationOptions.itemsPerPage = this.getActiveTabObject?.config?.pageSize || 100;
                }
                this.getClipBoardTickets();
            } else {
                if (loadTickets) {
                    if (this.paginationOptions.itemsPerPage !== this.getActiveTabObject?.config?.pageSize){
                        this.paginationOptions.itemsPerPage = this.getActiveTabObject?.config?.pageSize || 100;
                        this.paginationOptions.page = this.getActiveTabObject?.page || 1;
                        this.getTicketsFirstTime = false;
                    } else{

                        await this.getTickets();
                    }
                }
            }

            const tabSelectedItems = this.$store.getters.tabSelectedItems;
            if(tabSelectedItems) {
                const tempArray = tabSelectedItems[id];
                this.selected = Array.isArray(tempArray) ? tempArray : [];
            } else {
                console.error('no tabSelectedItems');
            }
        },
        getClipBoardTickets() {
            let clipboard = this.clipboardItems;
            // Update this.headers based on ticket tab fields
            this.syncTicketTableHeader();

            // Format tickets for table
            this.tickets = clipboard.map(ticket => {
                this.formatTicketForTable(ticket);
                return ticket;
            });

            this.selected = [...this.selected];
            this.totalTicketsCount = clipboard.length;
            this.pageCount = 100;
            this.hideFooter = true;
        },
        syncTicketTable: function() {
            // Set the default sort order when the sort order is cleared
            const options = this.getActiveTabObject;                        
            if (options.config?.orderBy){
                if (options.config.orderBy.includes('-')) {                                
                    const orderByArray = options.config.orderBy.split('-');
                    this.orderBy = orderByArray[0];
                    this.order = orderByArray[1];
                }else{
                    this.orderBy = 'ticket';  
                }
            }else{
                this.orderBy = 'ticket';
            } 
            this.getTickets();
        },
        syncTicketTableHeader(){
            let fields = this.getActiveTabObject.config?.fields;
            if(!fields?.length) {
                fields = 'center,TR_member_code,type,TR_seq_date,TR_seq_num,TR_locator,county,place,company,work_type';
            }
            fields = fields.split(',');
            let newHeaders = [];
            const staticFields = [
                'ticket',
                'revision',
            ];
            // if fields contains duedate, insert dueDateText into fields array ahead of duedate

            if(fields.includes('duedate')) {
                fields.splice(fields.indexOf('duedate'), 0, 'dueDateText');               
            }
            newHeaders.push(Object.assign({},this.headers[0]));  // clipboard column
            newHeaders.push(Object.assign({},this.headers[1]));  // ticket column
            
            for(let field of fields) {
                if(staticFields.includes(field)) {
                    continue;
                }
               
                const header = fieldHeaderMap[field];

                if(!header) {
                    console.error('syncTicketTableHeader could not sync field ', field);
                    continue;
                }
                newHeaders.push({
                    text: header.text,
                    value: field,
                    align: 'left',
                    sortable: true,
                    width: header.width
                });
            }
            this.headers = newHeaders;
        },
        checkClipboard: function() {
            return true;
        },
        defaultSort: function(orderBy) {
            if (orderBy.includes('-')) {              
                const orderByArray = orderBy.split('-');
                orderBy = orderByArray[0];
                this.order = orderByArray[1];               
            }                    
            // Set default sort on the client side
            if(orderBy) {
                // if not found use the default
                if(this.isValueInSqlColName(orderBy)){
                    this.orderBy = orderBy;
                } else {
                    // map custom table header to sql column name not found in the column catalog
                    switch(orderBy) {
                        case 'ticketRevision':
                            this.orderBy = 'ticket';
                            break;
                        case 'dueDateText':
                        case 'due_date':
                            this.orderBy = 'duedate';
                            break;
                        case 'seq_date':
                            this.orderBy = 'TR_seq_date';
                            break;
                        case 'updated':
                            this.orderBy = 'TS_updated';
                            break;
                        default:
                            // use the orderBy from userOptions.config
                            if (this.orderBy && this.orderBy.includes('-')) {
                                const orderByArray = this.orderBy.split('-');
                                this.orderBy = orderByArray[0];
                                this.order = orderByArray[1];                               
                            } else {
                                // Handle the case where this.orderBy is null or does not contain a '-'
                                this.orderBy = 'TR_seq_date';
                                this.order = 'asc';
                            }
                    }
                }
            } else {           
                // use the orderBy from userOptions.config
                if (this.orderBy && this.orderBy.includes('-')) {
                    const orderByArray = this.orderBy.split('-');
                    this.orderBy = orderByArray[0];
                    this.order = orderByArray[1];              
                } else {
                    // Handle the case where this.orderBy is null or does not contain a '-'
                    this.orderBy = 'TR_seq_date';
                    this.order = 'asc';
                }
            }
        },
        isValueInSqlColName(value) {

            let ticketColumnsCatalog = this.$store.getters.ticketColumnsCatalog;
            for (let i = 0; i < ticketColumnsCatalog.length; i++) {
                if (ticketColumnsCatalog[i].sql_col_name === value) {
                    return true;
                }
            }
            return false;       
        },
        async applyTicketSearchFilter() {
            try {
                // Close filter
                this.showSearchFilter=false;

                const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
                let options = [];
                // options.push([JSONTAB, await getUserOption(JSONTAB)]);
                options.push([JSONTAB, JSON.stringify(this.getActiveTabObject)]);

                let optionValue = options[0][1];
                optionValue = JSON.parse(optionValue);
                                const updatedSearchFilters = this.mapSearchFilter(this.searchFilter);

                // New settings
                optionValue._TIMESTAMP_ = (new Date()).toISOString();
                optionValue.config.filters = updatedSearchFilters;
                options[0][1] = JSON.stringify(optionValue, undefined, 2);

                // Save options to backend via store action
                await this.$store.dispatch('updateUserOptions', options);
                
                // Reset the page number when the filter is applied
                this.page = 1;
                await this.getTickets();
            } catch(error) {
                console.error('applyTicketSearchFilter error ', error);
            }
        },
        mapSearchFilter(searchFilterObj){
            let filter = {
                'center': searchFilterObj.Center ? searchFilterObj.Center : '*',
                'member_code': searchFilterObj.MemberCode && searchFilterObj.MemberCode.length > 0 ? searchFilterObj.MemberCode.join(',') : '*',
                'seq_date': searchFilterObj.SeqDate && searchFilterObj.SeqDate.length ? searchFilterObj.SeqDate.join(':'):'*',
                'priority': searchFilterObj.Priority ? searchFilterObj.Priority:'*',
                'risk': searchFilterObj.Risk ? searchFilterObj.Risk:'*',
                'type': searchFilterObj.TicketType ? searchFilterObj.TicketType:'*',
                'category': searchFilterObj.Category ? searchFilterObj.Category:'*',
                'TR_stage': searchFilterObj.Stage ? searchFilterObj.Stage:'*',
                'fips': searchFilterObj.Counties && searchFilterObj.Counties.length > 0 ? searchFilterObj.Counties.join(',') : '',
                'locator': searchFilterObj.Locator ? searchFilterObj.Locator:'*',
                'due': searchFilterObj.Due ? searchFilterObj.Due:'*',
                'ticket': searchFilterObj.TicketNumber ? searchFilterObj.TicketNumber : '',
                'place': searchFilterObj.Place ? searchFilterObj.Place : '',
                'subdivision': searchFilterObj.Subdivision ? searchFilterObj.Subdivision : '',
                'workdate': searchFilterObj.WorkDate && searchFilterObj.WorkDate.length ? searchFilterObj.WorkDate.join(':'): '*',
                'updated': searchFilterObj.UpdateDate && searchFilterObj.UpdateDate.length ? searchFilterObj.UpdateDate.join(':'): '*',
                'grid': searchFilterObj.Grid ? searchFilterObj.Grid : '',
                'address': searchFilterObj.Address ? searchFilterObj.Address : '',
                'street': searchFilterObj.Street ? searchFilterObj.Street.trim() : '',
                'cross': searchFilterObj.Cross ? searchFilterObj.Cross.trim() : '',
                'company': searchFilterObj.Company ? searchFilterObj.Company : '',
                'caller': searchFilterObj.Caller ? searchFilterObj.Caller : '',
                'donefor': searchFilterObj.DoneFor ? searchFilterObj.DoneFor : '',
                'location': searchFilterObj.Location ? searchFilterObj.Location : '',
                'worktype': searchFilterObj.WorkType ? searchFilterObj.WorkType : '',
                'comment': searchFilterObj.Comment ? searchFilterObj.Comment : ''
            };
            return filter;
        },
        setSelectedSearchFilter(currentFilters){
            this.searchFilter.Center = currentFilters.center !== '*' ? currentFilters.center : '';
            this.searchFilter.MemberCode = currentFilters.member_code !== '*' && currentFilters.member_code.length ? currentFilters.member_code.split(',') : '';
            this.searchFilter.SeqDate = currentFilters.seq_date !== '*' && currentFilters.seq_date.length ? currentFilters.seq_date.split(':') : [];
            this.searchFilter.Stage = currentFilters.TR_stage !== '*' ? currentFilters.TR_stage : '';
            this.searchFilter.Priority = currentFilters.priority !== '*' ? currentFilters.priority : '';
            this.searchFilter.Risk = currentFilters.risk !== '*' ? currentFilters.risk : '';
            this.searchFilter.TicketType = currentFilters.type !== '*' ? currentFilters.type : '';
            this.searchFilter.Category = currentFilters.category !== '*' ? currentFilters.category : '';
            this.searchFilter.Counties = currentFilters.fips !== '*' && currentFilters.fips.length ? currentFilters.fips.split(',') : '';
            this.searchFilter.Locator = currentFilters.locator !== '*' ? currentFilters.locator : '';
            this.searchFilter.Due = currentFilters.due !== '*' ? currentFilters.due : '';
            this.searchFilter.TicketNumber = currentFilters.ticket ? currentFilters.ticket : '';
            this.searchFilter.Place = currentFilters.place ? currentFilters.place : '';
            this.searchFilter.Subdivision = currentFilters.subdivision ? currentFilters.subdivision : '';
            this.searchFilter.WorkDate = currentFilters.workdate !== '*' && currentFilters.workdate?.length ? currentFilters.workdate.split(':'): [];
            this.searchFilter.UpdateDate = currentFilters.updated !== '*' && currentFilters.updated?.length ? currentFilters.updated.split(':'): [];
            this.searchFilter.Grid = currentFilters.grid ? currentFilters.grid : '';
            this.searchFilter.Address = currentFilters.address ? currentFilters.address : '';
            this.searchFilter.Street = currentFilters.street ? currentFilters.street : '';
            this.searchFilter.Cross = currentFilters.cross ? currentFilters.cross : '';
            this.searchFilter.Company = currentFilters.company ? currentFilters.company : '';
            this.searchFilter.Caller = currentFilters.caller ? currentFilters.caller : '';
            this.searchFilter.DoneFor = currentFilters.donefor ? currentFilters.donefor : '';
            this.searchFilter.Location = currentFilters.location ? currentFilters.location : '';
            this.searchFilter.WorkType = currentFilters.worktype ? currentFilters.worktype : '';
            this.searchFilter.Comment = currentFilters.comment ? currentFilters.comment : '';

            let filterChips = {};
            for(let s in this.searchFilter) {
                if(this.searchFilter[s] && this.searchFilter[s].length) {
                    if(['SeqDate', 'WorkDate', 'UpdateDate'].includes(s)) {
                        filterChips[s] = this.searchFilter[s].join(':');
                    } else if(['MemberCode','Counties'].includes(s)) {
                        filterChips[s] = this.searchFilter[s].join(',');
                    } else {
                        filterChips[s] = this.searchFilter[s];
                    }
                }
            }
            this.filterChips = filterChips;
        },
        removeFilter(filterName) {
            if(['SeqDate', 'WorkDate', 'UpdateDate'].includes(filterName)) {
                this.searchFilter[filterName] = [];
            } else {
                this.searchFilter[filterName] = '';
            }
            this.applyTicketSearchFilter();
        },
        clearFilters() {
            const tmpFilters = { ...this.searchFilter };
            for(let tmpFilter in tmpFilters) {
                this.searchFilter[tmpFilter] = '';
            }
        },
        clearFiltersAndApply() {
            this.showSearchFilter=false;
            const tmpFilters = { ...this.searchFilter };
            for(let tmpFilter in tmpFilters) {
                this.searchFilter[tmpFilter] = '';
            }
            this.applyTicketSearchFilter();
        },
        setSearchFilterApplied() {
            let flag = false;
            for (let field in this.searchFilter) {
                if (this.searchFilter[field] !== '') {
                    flag = true;
                }
            }
            this.searchFilterApplied = flag;
        },
        truncateField(field) {
            if (field.length > 45) {
                return field.substring(0, 45) + '...';
            }
            return field;
        },
        async updateItemsPerPage() {
            try {
                const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
                let options = [];
                // options.push([JSONTAB, await getUserOption(JSONTAB)]);
                options.push([JSONTAB, JSON.stringify(this.getActiveTabObject)]);

                let optionValue = options[0][1];
                optionValue = JSON.parse(optionValue);

                // New settings
                optionValue._TIMESTAMP_ = (new Date()).toISOString();
                optionValue.config.pageSize = this.paginationOptions.itemsPerPage;
                options[0][1] = JSON.stringify(optionValue, undefined, 2);

                // Save options to backend via store action
                await this.$store.dispatch('updateUserOptions', options);

                // this.getTickets();
            } catch(error) {
                console.error('updateItemsPerPage error ', error);
            }
        },
        async updatePage() {
            try {
                const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
                let options = [];
                // options.push([JSONTAB, await getUserOption(JSONTAB)]);
                options.push([JSONTAB, JSON.stringify(this.getActiveTabObject)]);

                let optionValue = options[0][1];
                optionValue = JSON.parse(optionValue);

                // New settings
                optionValue._TIMESTAMP_ = (new Date()).toISOString();
                optionValue.page = this.paginationOptions.page;
                options[0][1] = JSON.stringify(optionValue, undefined, 2);

                // Save options to backend via store action
                await this.$store.dispatch('updateUserOptions', options);

                // this.getTickets();
            } catch(error) {
                console.error('updateItemsPerPage error ', error);
            }
        },
        async resetPages(){
            try {
                const existingTabs = [...this.ticketTabs];
                for(let tab of existingTabs) {
                    if(tab.tabId.startsWith('open-tickets')) {
                        const JSONTAB = 'JSON:TAB:' + tab.tabId;

                        let options = [];
                        options.push([JSONTAB, await getUserOption(JSONTAB)]);

                        let optionValue = options[0][1];
                        if(!optionValue || !optionValue.length) {
                            continue;
                        }
                        optionValue = JSON.parse(optionValue);

                        // New settings
                        optionValue._TIMESTAMP_ = (new Date()).toISOString();
                        optionValue.page = 1;
                        options[0][1] = JSON.stringify(optionValue, undefined, 2);

                        // Save options to backend via store action
                        if (document.cookie.startsWith('newtin=')) {
                            await this.$store.dispatch('updateUserOptions', options);
                        }
                    }
                }
            } catch(error) {
                console.error('resetPages error ', error);
            }
        },
        async refreshTicketTab() {
            if(!this.activeTab.tabId) {
                this.selectedTab('open-tickets','Tickets');
            }else{
                if (this.activeTab.tabId === 'tickets-clipboard') {
                    const result = await webtmsBackend.getUserClipboard();
                    this.$store.commit('SET_USER_CLIPBOARD', result.userClipboard);
                }
                this.selectedTab(this.activeTab.tabId, this.activeTab.label);
            }
            this.setTicketTabCounts();
            // this.forceRerender();
            this.timerKey += 1;
        },
        selectAllToggle(props) {
            // Clear all selected items if selection is cleared
            if(!props.value) {
                this.selected = [];
            }
        },
        ticketsToBeReopened() {
            let ticketsToBeReopened = [];
            if(this.selected.length) {
                // Filter out tickets that are already reopened
                ticketsToBeReopened = this.selected.filter(ticket => ticket.TR_stage === 'CT');
            }
            return ticketsToBeReopened;
        },
        locatorName(account) {
            if(!account) {
                return '';
            }
            let locatorName = '';
            let data = this.$store.getters.companyUsers;
            let locators = data.filter(item => { return item.ingroup.includes('Locator');});
            let locator = locators.find(item => item.account === account);
            if(locator) {
                locatorName = locator.name.toUpperCase();
            }
            return `${account} : ${locatorName}`;
        },
        showConfirmRemoveTab(selectedTab){
            this.selectedTabForDelete = selectedTab;
            this.showConfirmRemoveTabModal = true;
        },
        closeRemoveTabModal(){
            this.showConfirmRemoveTabModal = false;
            this.selectedTabForDelete = null;
        }
    },
};
</script>
<style >
#tickets-page .v-toolbar__content {
padding-left: 0;
padding-right: 0;
}
#tickets-page .toolbar-title {
    font-size: 20px;
    font-weight: 500;
}
#tickets-page .v-icon.v-icon.v-icon--link {
    color: #122448ed;
}
#main-tickets-table.v-data-table {
    border: 1px solid #ccc;
    width: 100%;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    overflow: hidden;
}
#main-tickets-table.v-data-table__wrapper {
    border-radius: 4px;
}
#main-tickets-table table tr:last-child td {
    border-bottom: 1px solid #ccc;
}
#main-tickets-table table tr td:nth-child(1),
#main-tickets-table table tr th:nth-child(1),
#main-tickets-table table tr td:nth-child(2),
#main-tickets-table table tr th:nth-child(2),
#main-tickets-table table tr td:nth-child(3),
#main-tickets-table table tr th:nth-child(3){

    position: sticky !important;
    position: -webkit-sticky !important;
    z-index: 2;
}

#main-tickets-table table tr td:nth-child(1),
#main-tickets-table table tr th:nth-child(1) {
    left: 0;
    border-radius:0;
}
#main-tickets-table table tr td:nth-child(2),
#main-tickets-table table tr th:nth-child(2) {
    left: 64px; 
}
#main-tickets-table table tr td:nth-child(3),
#main-tickets-table table tr th:nth-child(3) {
    left: calc(64px + 64px);
    border-right:3px solid rgba( 0, 0, 0, 0.04 );
}

#main-tickets-table table tr th:nth-child(1),
#main-tickets-table table tr th:nth-child(2),
#main-tickets-table table tr th:nth-child(3)
{
    z-index: 3!important;
}
.status-open-chip.v-chip.v-chip--outlined.v-chip.v-chip{
    background-color: rgba(73, 200, 79, .1) !important;
    padding: 0 6px;
}
.status-open-cancelled-chip.v-chip.v-chip--outlined.v-chip.v-chip {
    background-color: rgba(73, 200, 79, .1) !important;
    padding: 0 6px;
    text-decoration: line-through;
}
.status-cancelled-ticketrevision {
    text-decoration: line-through;
    color: #1976D2;
}
.status-open-ticketrevision {
    text-decoration: none;
    color: #1976D2;
}
.status-closed-chip.v-chip.v-chip--outlined.v-chip.v-chip{
    background-color: rgba(255, 179, 0, .1) !important;
    padding: 0 6px;
}
#main-tickets-table thead,
#main-tickets-table th {
    background-color: #F5F7FA;
}
#main-tickets-table td {
    background:  #fff;
}
#main-tickets-table .v-data-table__selected td {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #F26227;
}

.title {
  font-size: 2rem !important;
}
.ticket-chip {
  border-radius: 20px !important;
  height: 18px !important;
  padding: 0px 4px !important;
}
.toggle-btn {
  height: 36px !important;
  width: 36px !important;
  margin: 0px !important;
}
.theme--light.v-btn.v-btn--disabled .v-icon.action-icon {
  color: #fff !important;
}
.action-btn .v-btn__content {
  color: #fff !important;
}
.ticket-tabs.theme--light.v-tabs > .v-tabs-bar {
   margin-bottom: 8px;  
}
.active-ticket-tab {
  background-color: #e6e8ee;
}
.v-tab,
.action-btn {
  text-transform: none !important;
}
.theme--light.v-btn.v-btn--disabled.v-btn--has-bg.action-btn {
  background-color: #d5d8de !important;
}
.toggle-btn-active {
  background-color: #303f5f !important;
}
.toggle-icon-active {
  color: #fff !important;
}
.toggle-icon-inactive {
  color: #303f5f !important;
}
#main-tickets-table table tr.brown-lightnen-1 td:nth-child(1),
#ticket-cards .brown-lightnen-1 {
    box-shadow: inset 6px 0px 0px #996633!important;
}
#main-tickets-table table tr.brown-darken-1 td:nth-child(1),
#ticket-cards .brown-darken-1 {
    box-shadow: inset 6px 0px 0px #663300!important;
}
#main-tickets-table table tr.purple-darken-1 td:nth-child(1),
#ticket-cards .purple-darken-1 {
    box-shadow: inset 6px 0px 0px #9c27b0!important;
}
#main-tickets-table table tr.purple-darken-2 td:nth-child(1),
#ticket-cards .purple-darken-2 {
    box-shadow: inset 6px 0px 0px #8E24AA!important;
}
#main-tickets-table table tr.purple-darken-3 td:nth-child(1),
#ticket-cards .green-darken-3 {
    box-shadow: inset 6px 0px 0px #680059!important;
}
#main-tickets-table table tr.blue-darken-1 td:nth-child(1),
#ticket-cards .blue-darken-1 {
    box-shadow: inset 6px 0px 0px #3366FF!important;
}
#main-tickets-table table tr.red-darken-1 td:nth-child(1),
#ticket-cards .red-darken-1 {
    box-shadow: inset 6px 0px 0px #E53935!important;
}
#main-tickets-table table tr.red-darken-2 td:nth-child(1),
#ticket-cards .red-darken-2 {
    box-shadow: inset 6px 0px 0px #B71C1C!important;
}
#main-tickets-table table tr.red-lighten-4 td:nth-child(1),
#ticket-cards .red-lighten-4 {
    box-shadow: inset 6px 0px 0px #f44336!important;
}
#main-tickets-table table tr.orange-darken-1 td:nth-child(1),
#ticket-cards .orange-darken-1 {
    box-shadow: inset 6px 0px 0px #ff9800!important;
}
#main-tickets-table table tr.orange-lighten-1 td:nth-child(1),
#ticket-cards .orange-lighten-1 {
    box-shadow: inset 6px 0px 0px #FF7043!important;
}
#main-tickets-table table tr.orange-lighten-2 td:nth-child(1),
#ticket-cards .orange-lighten-2 {
    box-shadow: inset 6px 0px 0px #FFAB91!important;
}
#main-tickets-table table tr.orange-lighten-3 td:nth-child(1),
#ticket-cards .orange-lighten-3 {
    box-shadow: inset 6px 0px 0px #FFCCBC!important;
}
#main-tickets-table table tr.yellow-darken-1 td:nth-child(1),
#ticket-cards .yellow-darken-1 {
    box-shadow: inset 6px 0px 0px #ffeb3b!important;
}
#main-tickets-table table tr.amber-darken-2 td:nth-child(1),
#ticket-cards .amber-darken-2 {
    box-shadow: inset 6px 0px 0px #FFB300!important;
}
#main-tickets-table table tr.light-green-lighten-1 td:nth-child(1),
#ticket-cards .light-green-lighten-1 {
    box-shadow: inset 6px 0px 0px #DCE775!important;
}
#main-tickets-table table tr.light-green-darken-1 td:nth-child(1),
#ticket-cards .light-green-darken-1 {
    box-shadow: inset 6px 0px 0px #8bc34a!important;
}
#main-tickets-table table tr.green-darken-1 td:nth-child(1),
#ticket-cards .green-darken-1 {
    box-shadow: inset 6px 0px 0px #00A65D!important;
}
#main-tickets-table-footer {
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    font-size: 14px;
}
.showing-tickets-text {
    margin-left: 11px;
}
#main-tickets-table-footer .v-pagination button,
#mobile-footer-pagination .v-pagination button {
    box-shadow:none;
    min-width: 20px;
    height: 20px;
    padding: 0;
    font-size: 14px;
    line-height: 20px;
}
.pagination-select {
    border: 1px solid #ccc;
    border-radius:4px;
    margin: 2px 12px;
    position: relative;
}
.pagination-select select {
    width: 60px;
    padding: 4px 8px;
}
.pagination-select .pagination-select-icon {
    position: absolute;
    right: 0px;
    pointer-events: none;
    padding: 4px 0px;
}
.pagination-container {
    width: 400px;
}
.configTitle {
    border: 1px solid #ccc;
}
.v-btn.dialogBtn {
    width: 86px;
    height: 48px;
    margin: 0px;
    padding: 0px;
    border-radius: 4px;
}
.open-search-filter {
    min-width: 233px;
    height: 100%;
    flex-grow: unset;
    
}
.cancel-search {
    display: none;
}
.cancel-search .v-btn__content {
    color: #122448 !important;
}

.open-search-mobile-filter {
    position: fixed !important;
    z-index: 9 !important;
    top: 0 !important;
    left: 0;
    width: 100vw !important;
    padding: 0 !important;
}
.filter-mobile-header {
    justify-content: space-between !important;
    padding: 20px;
    height: 48px !important;
    background-color: #fff !important;
}
.filter-header .close-mobile-filter {
    display: none;
}
.filter-mobile-body {
    height: calc(100vh - 110px) !important;
}
.cancel-mobile-search {
    display: block;
}
.filter-mobile-btn {
    width: 40% !important;
    height: 48px !important;
}
.filter-mobile-footer {
    height: 80px !important;
}
.filter-header {
    width: 100%;
    height: 52px;
    padding: 9px, 8px, 8px, 12px;
    background-color: #f2f6fc;
    border: 1px solid #D5D8DE;
    border-radius: 4px 4px 0px 0px;
    justify-content: space-evenly;
}
.filter-header .filter-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0.001em;
    color: #787a7d;
}
.filter-title-mobile {
    font-weight: 500;
    font-size: 20px;
    line-height: 22px;
    letter-spacing: 0.001em;
    color: #787a7d;
}
.filter-header .clear-filter {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.0025em;
    color: #787a7d;
}
.filter-header .close-filter {
    font-size: 18px;
}
.filter-body {
    height: calc(100vh - 350px);
    overflow-y: auto;
    background: #FFFFFF;
    border: 1px solid #D5D8DE;
    padding: 10px;
}
.filter-footer {
    padding: 2px;
    height: 51px;
    background: #FFFFFF;
    border: 1px solid #D5D8DE;
    border-radius: 0px 0px 4px 4px;
    justify-content: space-around;
}
.filter-footer .filter-btn {
    width: 210px;
}
.open-search-table-section {
    width: calc(100% - 250px) !important;
}
.clipboard-controls {
    display:flex;
    align-items: center;
    width: 100%;
}
.v-card__title .v-icon {
    background-color: #F0F6FF;
    margin-right: 12px;
    border-radius: 50%;
    width: 32px;
    height: 32px;
}
.control-title {
    margin-top: 16px;
    font-weight: 500;
    font-size: 16px;
    color: #122448;
}
.v-card__title span {
    font-size: 20px;
}
.v-radio label {
    color: #122448;
}
.v-tab {
    max-width: 100% !important;
}
.mobile-company-header {
    font-size: 20px;
    font-weight: 500;
    color: #35383F;
}
.mobile-ticket-action-bar {
    position: fixed;
    bottom: 0px;
    width: 100%;
    text-align: center;
}
.mobile-ticket-action-bar .v-btn {
    width: 140px;
}
#ticket-selection-error-dialog .ticket-section-error-mobile-title {
    font-size: 16px;
}
.cancel-search {
    display: none;
}
.cancel-search .v-btn__content {
    color: #122448 !important;
}

.open-search-mobile-filter {
    position: fixed !important;
    z-index: 9 !important;
    top: 0 !important;
    left: 0;
    width: 100vw !important;
    padding: 0 !important;
}
.filter-mobile-header {
    justify-content: space-between !important;
    padding: 20px;
    height: 48px !important;
    background-color: #fff !important;
}
.filter-header .close-mobile-filter {
    display: none;
}
.filter-mobile-body {
    height: calc(100vh - 110px) !important;
}
.cancel-mobile-search {
    display: block;
}
.filter-mobile-btn {
    width: 40% !important;
    height: 48px !important;
}
.filter-mobile-footer {
    height: 80px !important;
}
.filter-header {
    width: 100%;
    height: 52px;
    padding: 9px, 8px, 8px, 12px;
    background-color: #f2f6fc;
    border: 1px solid #D5D8DE;
    border-radius: 4px 4px 0px 0px;
    justify-content: space-evenly;
}
.filter-header .clear-filter {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.0025em;
    color: #787a7d;
}
.filter-header .close-filter {
    font-size: 18px;
}
.filter-body {
    height: calc(100vh - 350px);
    overflow-y: auto;
    background: #FFFFFF;
    border: 1px solid #D5D8DE;
    padding: 10px;
}
.filter-footer {
    padding: 2px;
    height: 51px;
    background: #FFFFFF;
    border: 1px solid #D5D8DE;
    border-radius: 0px 0px 4px 4px;
    justify-content: space-around;
}
.filter-footer .filter-btn {
    width: 210px;
}
.open-search-table-section {
    width: calc(100% - 250px) !important;
}
.v-card__title .v-icon {
    background-color: #F0F6FF;
    margin-right: 12px;
    border-radius: 50%;
    width: 32px;
    height: 32px;
}
.control-title {
    margin-top: 16px;
    font-weight: 500;
    font-size: 16px;
    color: #122448;
}
.v-card__title span {
    font-size: 20px;
}
.v-radio label {
    color: #122448;
}
.v-tab {
    max-width: 100% !important;
}
.no-data {
    margin: 0;
    background-color: #FFFFFF;
    color: rgba(0, 0, 0, 0.87);
    border: 1px solid #ccc;
    /* width: 100%; */
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    overflow: hidden;
    height: calc(100vh - 380px)
}
.no-data span {
    display: block;
}
.no-data span:nth-child(2) {
    font-weight: 500;
    color: #35383F;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: 0.0015em;
}
.no-data span:nth-child(3) {
    font-weight: 400;
    color: #7F8592;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.0025em;
}
.filter-chips {
    margin-bottom: 5px;
}
.custom-refresh-btn {
    height: 30px;
    width: 30px;
}
.filter-area {
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
}
.filter-chips {
    max-width: 100%;
    padding: 0;
    margin: 0;
    display: inline-flex;
    overflow: auto;
}
.filter-chips .v-chip {
    display: table;
}
.filter-mobile-chips {
    padding: 0;
    margin: 0;
    margin-bottom: 5px;
    max-width: 90%;
    display: inline-flex;
    overflow: auto;
    flex-wrap: unset;
}
.filter-mobile-chips .v-chip {
    display: table;
}
.clipboard-checkbox {
    margin-top: 0px;
    height: 34px;
}
.date-field {
     white-space: nowrap;
}
#main-tickets-table .v-data-footer__select {
    height: 34px;
}
</style>
